'use client'

import { Fragment } from 'react'
import { Spacer, Image } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { HorizontalScrollArea } from '@marketplace-web/common-components/horizontal-scroll-area-ui'
import { BundleSummaryModel, findThumbnail } from '@marketplace-web/bundles/items-bundle-data'
import { ProductItemModel } from '@marketplace-web/item-box/product-item-data'

import BundleFooter from '../../pages/BundleItems/BundleFooter'

enum ItemThumbnailSize {
  Medium = 'thumb150x210',
}

const getItemPhotoThumbnail = (
  photos:
    | Array<{
        isMain: boolean
        thumbnails: Array<{ type: ItemThumbnailSize | string; url: string }>
      }>
    | undefined,
  size = ItemThumbnailSize.Medium,
) => {
  const photo = photos?.find(({ isMain }) => isMain)

  return findThumbnail(photo, size)
}

export type Props = {
  selectedItems: Array<ProductItemModel>
  bundleSummary: BundleSummaryModel | undefined
  bundleTrackingId: string | null
  isSellerBusiness: boolean
  homepageSessionId: string | null
  sellerId: number
  isBundleSummaryLoading: boolean
  transactionId: number | null
  checkoutId?: string | null
  show?: boolean
  warnOnUnsavedChanges?: boolean
  screen: string
  onReviewBundleLoading: (value: boolean) => void
  escrowNoteTranslationKey?: string
}

const BundleFooterWrapper = ({
  selectedItems,
  bundleSummary,
  bundleTrackingId,
  isSellerBusiness,
  homepageSessionId,
  sellerId,
  isBundleSummaryLoading,
  transactionId,
  checkoutId,
  show = true,
  warnOnUnsavedChanges = false,
  screen,
  onReviewBundleLoading,
  escrowNoteTranslationKey,
}: Props) => {
  const translateA11y = useTranslate('a11y')

  if (!show) return null

  const renderPreFooter = () => {
    if (!selectedItems.length) return null

    return (
      <div
        className="container u-phones-only u-ui-padding-bottom-medium"
        data-testid="bundle-items-thumbnails"
      >
        <Spacer />
        <HorizontalScrollArea
          showControls={false}
          arrowLeftText={translateA11y('actions.move_left')}
          arrowRightText={translateA11y('actions.move_right')}
        >
          <div className="u-nowrap">
            {selectedItems.map(item => (
              <Fragment key={item.id}>
                <Image
                  key={item.id}
                  src={getItemPhotoThumbnail(item.photos)}
                  styling={Image.Styling.Rounded}
                  size={Image.Size.Medium}
                  scaling={Image.Scaling.Cover}
                />
                <Spacer orientation={Spacer.Orientation.Vertical} />
              </Fragment>
            ))}
          </div>
        </HorizontalScrollArea>
      </div>
    )
  }

  return (
    <div className="action-footer">
      {renderPreFooter()}
      <div className="container">
        <BundleFooter
          bundleSummary={bundleSummary}
          selectedItems={selectedItems}
          bundleTrackingId={bundleTrackingId}
          isSellerBusiness={isSellerBusiness}
          homepageSessionId={homepageSessionId}
          sellerId={sellerId}
          isBundleSummaryLoading={isBundleSummaryLoading}
          transactionId={transactionId}
          checkoutId={checkoutId}
          warnOnUnsavedChanges={warnOnUnsavedChanges}
          screen={screen}
          onReviewBundleLoading={onReviewBundleLoading}
          escrowNoteTranslationKey={escrowNoteTranslationKey}
        />
      </div>
    </div>
  )
}

export default BundleFooterWrapper
